import { useQuery } from "@tanstack/react-query";
import { FlexListSummary } from "@/components/listSummary";
import { getWarehouseCostSummary } from "@/helpers/apis";
import { WarehouseCostSummaryResponse } from "../types";
import { Typography } from "@altimateai/lego";
import { useTenantInfo } from "@/helpers/hooks";
import { useWarehouseFilters } from "../hooks/useWarehouseFilters";

const WarehouseCostSummary = () => {
  const { currency } = useTenantInfo();
  const { filterParams, isInitialized } = useWarehouseFilters();

  const { data: summaryData, isLoading } = useQuery({
    queryKey: ["warehouse-cost-summary", filterParams],
    queryFn: () => getWarehouseCostSummary(filterParams),
    retry: false,
    enabled: isInitialized,
  });

  // Transform API response to FlexListSummary format
  const summaryItems = summaryData
    ? Object.entries(summaryData as WarehouseCostSummaryResponse)
        .filter(([_, item]) => item.display)
        .map(([key, item]) => {
          if (key === "realised_savings") {
            // Format range display for realised savings
            const realisedSavingsItem = item as WarehouseCostSummaryResponse["realised_savings"];
            const value = new Intl.NumberFormat("en-US", {
              style: "currency",
              currency,
              minimumFractionDigits: 2,
            }).format(realisedSavingsItem.value);

            return {
              label: "Realised Savings",
              value: `${value}`,
            };
          }

          if (key === "estimated_annual_savings") {
            const formattedValue = new Intl.NumberFormat("en-US", {
              style: "currency",
              currency,
              minimumFractionDigits: 2,
            }).format(item.value);

            return {
              label: "Estimated Annual Savings",
              value: formattedValue,
            };
          }

          if (key === "total_opportunities") {
            return {
              label: "Total Opportunities",
              value: item.value.toString(),
            };
          }

          return {
            label: key,
            value: item.value.toString(),
          };
        })
    : [];

  if (!summaryItems.length) {
    return null;
  }

  return (
    <div className="al-border-b al-border-b-gray-200 al-pb-4">
      <Typography variant="section-heading" className="al-mb-2">
        Total Savings
      </Typography>
      <FlexListSummary summary={summaryItems} isLoading={isLoading} className="!al-shadow-none" />
    </div>
  );
};

export default WarehouseCostSummary;
